import { loadCatalog } from "./pageData";
import { trackAction } from "./engagement";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import styles from "./Home.module.css";
import SiteShell from "./SiteShell";
import LazyImage from "./LazyImage";
import seoHubs from "./seoHubs.json";
import Seo, {
  createBreadcrumbList,
  createCollectionPageSchema,
} from "./Seo";
import { getImagePagePath } from "./imageRoutes";
import { newestFirst } from "./imageOrdering";
import {
  createAlternateLinks,
  getCategoryByRoute,
  getCategoryPath,
  getBrowserLocale,
  getLocalizedPath,
  getSearchHints,
  localizeCategory,
  localizeImage,
  translate,
} from "./i18n";

const INITIAL_IMAGE_LIMIT = 48;
const IMAGE_LOAD_STEP = 48;
const EMPTY_LIST = [];

function getHubTranslation(hub, locale) {
  return hub.translations?.[locale] || hub.translations?.de;
}

function getHubPath(hub, locale) {
  const translation = getHubTranslation(hub, locale);
  return getLocalizedPath(`/themen/${translation.slug}`, locale);
}

function localizeHub(hub, locale) {
  const translation = getHubTranslation(hub, locale);

  return {
    ...hub,
    ...translation,
    path: getHubPath(hub, locale),
  };
}

function createFaqItems(t) {
  return [
    {
      question: t("faqFreeQuestion"),
      answer: t("faqFreeAnswer"),
    },
    {
      question: t("faqKidsQuestion"),
      answer: t("faqKidsAnswer"),
    },
    {
      question: t("faqPrintQuestion"),
      answer: t("faqPrintAnswer"),
    },
    {
      question: t("faqTopicsQuestion"),
      answer: t("faqTopicsAnswer"),
    },
    {
      question: t("faqNoLoginQuestion"),
      answer: t("faqNoLoginAnswer"),
    },
    {
      question: t("faqFormatsQuestion"),
      answer: t("faqFormatsAnswer"),
    },
    {
      question: t("faqPdfPacksQuestion"),
      answer: t("faqPdfPacksAnswer"),
    },
  ];
}

function createFaqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

const categoryHeroImageUrls = {
  unicorn: "images/unicorn/hero-2026.png",
  ostern: "images/ostern/hero-2026.png",
  sommer: "images/sommer/hero-2026.png",
  halloween: "images/halloween/hero-2026.png",
  dino: "images/dino/hero-2026.png",
  prinzessin: "images/prinzessin/hero-2026.png",
  xmas: "images/xmas/hero-2026.png",
  weltall: "images/weltall/hero-2026.png",
  fantasie: "images/fantasie/hero-2026.png",
  tiere: "images/tiere/hero-2026.png",
  essen: "images/essen/hero-2026.png",
  natur: "images/natur/hero-2026.png",
  soccer: "images/soccer/seo-2026-worldcup-01-fussball-turnier-finale.png",
  mandalas: "images/mandalas/01-sonnenblumen-mandala.png",
};

function Home({ forcedLocale }) {
  const [data, setData] = useState({ categories: [], images: [] });
  const [status, setStatus] = useState("loading");
  const [query, setQuery] = useState("");
  const [difficulty, setDifficulty] = useState(new URLSearchParams(window.location.search).get("difficulty") || "");
  const [visibleLimit, setVisibleLimit] = useState(INITIAL_IMAGE_LIMIT);
  const { categoryTag } = useParams();
  const locale = forcedLocale || getBrowserLocale();
  const t = (key, values) => translate(locale, key, values);
  const localPath = (path) => getLocalizedPath(path, locale);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const json = await loadCatalog(locale, categoryTag, controller.signal);
        setData(json);
        setStatus("ready");
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        setStatus("error");
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [locale, categoryTag]);

  useEffect(() => {
    setVisibleLimit(INITIAL_IMAGE_LIMIT);
  }, [categoryTag, query, difficulty]);

  const rawCategories = data.categories || EMPTY_LIST;
  const rawImages = data.images || EMPTY_LIST;
  const categories = useMemo(
    () => rawCategories.map((item) => localizeCategory(item, locale)),
    [rawCategories, locale]
  );
  const images = useMemo(
    () =>
      newestFirst(rawImages.map((item) => localizeImage(item, locale))),
    [rawImages, locale]
  );
  const activeCategories = useMemo(
    () => categories.filter((item) => !item.inactive),
    [categories]
  );
  const category = useMemo(
    () =>
      categoryTag
        ? getCategoryByRoute(activeCategories, categoryTag) ||
          getCategoryByRoute(categories, categoryTag)
        : null,
    [activeCategories, categories, categoryTag]
  );
  const categoryNames = useMemo(
    () =>
      categories.reduce((lookup, item) => {
        lookup[item.tag] = item.name;
        return lookup;
      }, {}),
    [categories]
  );
  const invalidCategory = Boolean(categoryTag) && status === "ready" && !category;
  const filteredImages = useMemo(
    () =>
      invalidCategory
        ? []
        : category
        ? images.filter((image) => image.tag === category.tag)
        : images,
    [category, images, invalidCategory]
  );
  const difficultyImages = useMemo(() => difficulty ? filteredImages.filter(image => image.difficulty === difficulty) : filteredImages, [filteredImages, difficulty]);
  const normalizedQuery = query.trim().toLowerCase();
  const visibleImages = useMemo(
    () =>
      normalizedQuery
        ? difficultyImages.filter((image) =>
            [image.title, image.description, categoryNames[image.tag], image.tag]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(normalizedQuery)
          )
        : difficultyImages,
    [categoryNames, difficultyImages, normalizedQuery]
  );
  const displayedImages = useMemo(
    () => visibleImages.slice(0, visibleLimit),
    [visibleImages, visibleLimit]
  );
  const hiddenImageCount = Math.max(visibleImages.length - displayedImages.length, 0);
  const categoryCounts = useMemo(() => Object.fromEntries(categories.map(item => [item.tag, item.imageCount ?? images.filter(image => image.tag === item.tag).length])), [categories, images]);
  const totalImages = data.totalImages ?? images.length;

  const pageTitle = invalidCategory
    ? t("categoryMissingTitle")
    : category
    ? t("categoryPageTitle", { category: category.name })
    : t("homePageTitle");
  const pageDescription = invalidCategory
    ? t("categoryMissingDescription")
    : category
    ? t("categoryDescription", { category: category.name })
    : t("homeDescription");
  const pagePath = category ? getCategoryPath(category, locale) : localPath("/");
  const heroTitle = category
    ? pageTitle.replace(/\s*\|\s*usmalbilder\.ch$/u, "")
    : t("homeTitle");
  const featuredCategories = useMemo(
    () =>
      activeCategories
        .filter((item) => categoryCounts[item.tag])
        .sort((a, b) => (categoryCounts[b.tag] || 0) - (categoryCounts[a.tag] || 0))
        .slice(0, 8),
    [activeCategories, categoryCounts]
  );
  const newestImages = useMemo(() => images.slice(0, 8), [images]);
  const topicHubs = useMemo(
    () => seoHubs.map((hub) => localizeHub(hub, locale)),
    [locale]
  );
  const pdfCategories = useMemo(
    () =>
      (category ? [category] : activeCategories)
        .filter((item) => item.pdfUrl)
        .sort((a, b) => (categoryCounts[b.tag] || 0) - (categoryCounts[a.tag] || 0))
        .slice(0, category ? 1 : 12),
    [activeCategories, category, categoryCounts]
  );
  const heroImages = useMemo(() => {
    const heroUrls = category
      ? [categoryHeroImageUrls[category.tag]]
      : activeCategories
          .map((item) => categoryHeroImageUrls[item.tag])
          .filter(Boolean)
          .slice(0, 4);

    return heroUrls
      .filter(Boolean)
      .map((downloadUrl) =>
        images.find((image) => image.downloadUrl === downloadUrl)
      )
      .filter(Boolean);
  }, [activeCategories, category, images]);
  const categoryIntro = category
    ? t("categoryIntro", { category: category.name })
    : t("homeIntro");
  const searchHints = getSearchHints(locale);
  const faqItems = createFaqItems(t);
  const alternateLinks = !invalidCategory
    ? createAlternateLinks((targetLocale) =>
        category ? getCategoryPath(category, targetLocale) : getLocalizedPath("/", targetLocale)
      )
    : [];

  return (
    <SiteShell locale={locale}>
      <Seo
        title={pageTitle}
        description={pageDescription}
        path={pagePath}
        alternates={alternateLinks}
        structuredData={[
          !invalidCategory &&
            createCollectionPageSchema({
              title: pageTitle,
              description: pageDescription,
              path: pagePath,
              items: filteredImages,
              getItemUrl: (item) => getImagePagePath(item, locale),
            }),
          createBreadcrumbList([
            { name: t("homeTitle"), path: localPath("/") },
            ...(category
              ? [{ name: category.name, path: getCategoryPath(category, locale) }]
              : []),
          ]),
          !category && !invalidCategory && createFaqSchema(faqItems),
        ]}
      />
      <section className={styles.galleryOnlyLayout}>
        <div className={styles.heroBand}>
          <div className={styles.heroContent}>
            <div className={styles.heroCopy}>
              <h1 className={styles.heroTitle}>{heroTitle}</h1>
              <p className={styles.heroText}>{categoryIntro}</p>
            </div>

            <label className={styles.heroSearch}>
              <span>{t("searchLabel")}</span>
              <input
                aria-label={t("searchAria")}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("searchPlaceholder")}
                type="search"
                value={query}
              />
            </label>

            <div className={styles.searchHints} aria-label={t("searchHintsAria")}>
              {searchHints.map((item) => (
                <button
                  key={item}
                  onClick={() => setQuery(item)}
                  type="button"
                >
                  {item}
                </button>
              ))}
            </div>

            <div className={styles.heroPromises} aria-label={t("heroPromisesAria")}>
              {[t("free"), t("instantPrint"), t("noLogin"), t("printFormats")].map(
                (promise) => (
                  <span key={promise}>{promise}</span>
                )
              )}
            </div>
          </div>

          <div className={styles.heroShowcase} aria-label={t("heroShowcaseAria")}>
            {heroImages.map((image) => (
              <Link
                className={styles.heroPreview}
                key={image.downloadUrl}
                to={getImagePagePath(image, locale)}
              >
                <LazyImage
                  alt={image.description}
                  className={styles.heroPreviewImage}
                  eager
                  height={image.thumbnailHeight}
                  src={`/${image.thumbnailUrl}`}
                  title={image.title}
                  width={image.thumbnailWidth}
                />
                <span>{image.title}</span>
              </Link>
            ))}
          </div>

          <div className={styles.heroStats} aria-label={t("statsAria")}>
            <span>
              <strong>{totalImages}</strong>
              {t("statImages")}
            </span>

            <span>
              <strong>{activeCategories.length}</strong>
              {t("statCategories")}
            </span>
            <span>
              <strong>A4</strong>
              {t("statPrint")}
            </span>
          </div>
        </div>

        <div className={styles.filterPanel}>
          <div className={styles.filterChips}>
            <Link
              className={`${styles.categoryChip} ${
                !categoryTag ? styles.categoryChipActive : ""
              }`}
              to={localPath("/")}
            >
              <span className={styles.categoryInfo}>
                <span className={styles.categoryName}>{t("all")}</span>
              </span>
              <span className={styles.categoryCount}>{totalImages}</span>
            </Link>

            {activeCategories.map((item) => (
              <Link
                key={item.tag}
                className={`${styles.categoryChip} ${
                  category && category.tag === item.tag ? styles.categoryChipActive : ""
                }`}
                to={getCategoryPath(item, locale)}
              >
                <span className={styles.categoryInfo}>
                  <span className={styles.categoryName}>{item.name}</span>
                </span>
                <span className={styles.categoryCount}>
                  {categoryCounts[item.tag] || 0}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className={styles.galleryPanel}>
          <div className={styles.galleryHeader}>
            <label className={styles.difficultyFilter}>
              {t("difficultyLabel")}
              <select value={difficulty} onChange={event => { setDifficulty(event.target.value); trackAction("difficulty_filter", {tag: category?.tag}, locale); }}>
                <option value="">{t("difficultyAll")}</option>
                <option value="easy">{t("difficultyEasy")}</option>
                <option value="medium">{t("difficultyMedium")}</option>
                <option value="detailed">{t("difficultyDetailed")}</option>
              </select>
            </label>
            <div>
              <h2 className={styles.galleryTitle}>
                {invalidCategory
                  ? t("categoryMissingTitle").replace(" | usmalbilder.ch", "")
                  : category
                  ? category.name
                  : t("allImages")}
              </h2>
            </div>
            <div className={styles.galleryCountPill}>
              {t("total", { count: filteredImages.length })}
            </div>
          </div>

          {status === "loading" && (
            <div className={styles.loadingGrid} aria-hidden="true">
              {Array.from({ length: 8 }).map((_, index) => (
                <div className={styles.loadingCard} key={index} />
              ))}
            </div>
          )}

          {status === "error" && (
            <div className={styles.errorCard}>
              <h3 className={styles.errorTitle}>{t("galleryErrorTitle")}</h3>
              <p className={styles.errorText}>{t("galleryErrorText")}</p>
            </div>
          )}

          {status === "ready" && visibleImages.length === 0 && (
            <div className={styles.emptyState}>
              <h3 className={styles.emptyTitle}>
                {invalidCategory
                  ? t("missingCategoryTitle")
                  : normalizedQuery
                  ? t("noSearchTitle")
                  : t("emptyTitle")}
              </h3>
              <p className={styles.emptyText}>
                {invalidCategory
                  ? t("missingCategoryText")
                  : normalizedQuery
                  ? t("noSearchText")
                  : t("emptyText")}
              </p>
            </div>
          )}

          {status === "ready" && visibleImages.length > 0 && (
            <>
              <div className={styles.galleryGrid}>
                {displayedImages.map((image, index) => (
                  <article
                    className={styles.galleryCard}
                    key={image.downloadUrl}
                    style={{
                      "--stagger": `${Math.min(index, 11) * 35}ms`,
                    }}
                  >
                    <Link
                      className={styles.galleryLink}
                      to={getImagePagePath(image, locale)}
                    >
                      <div className={styles.galleryMedia}>
                        <LazyImage
                          alt={image.description}
                          className={styles.colorPrintImage}
                          height={image.thumbnailHeight}
                          src={`/${image.thumbnailUrl}`}
                          title={image.title}
                          width={image.thumbnailWidth}
                        />
                      </div>
                      <div className={styles.galleryBody}>
                        <span className={styles.galleryTag}>
                          {categoryNames[image.tag] || image.tag}
                        </span>
                        <h3 className={styles.cardTitle}>{image.title}</h3>
                        <p className={styles.cardDescription}>{image.description}</p>
                        <span className={styles.cardCta}>{t("openPreview")}</span>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>

              {hiddenImageCount > 0 && (
                <div className={styles.loadMoreRow}>
                  <button
                    className={styles.loadMoreButton}
                    onClick={() =>
                      setVisibleLimit((currentLimit) => currentLimit + IMAGE_LOAD_STEP)
                    }
                    type="button"
                  >
                    {t("loadMore", {
                      count: Math.min(hiddenImageCount, IMAGE_LOAD_STEP),
                    })}
                  </button>
                  <span>
                    {t("visibleCount", {
                      visible: displayedImages.length,
                      total: visibleImages.length,
                    })}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {!category && featuredCategories.length > 0 && (
          <section className={styles.topicGrid} aria-labelledby="top-themen">
            <h2 id="top-themen" className={styles.sectionTitle}>
              {t("featuredTopics")}
            </h2>
            <div className={styles.topicCards}>
              {featuredCategories.map((item) => (
                <Link
                  className={styles.topicCard}
                  key={item.tag}
                  to={getCategoryPath(item, locale)}
                >
                  <span className={styles.topicTitle}>{item.name}</span>
                  <span className={styles.topicText}>{item.description}</span>
                  <span className={styles.topicCount}>
                    {t("topicCount", { count: categoryCounts[item.tag] })}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {!category && topicHubs.length > 0 && (
          <section className={styles.topicGrid} aria-labelledby="such-themen">
            <h2 id="such-themen" className={styles.sectionTitle}>
              {t("topicHubsTitle")}
            </h2>
            <p className={styles.sectionIntro}>{t("topicHubsText")}</p>
            <div className={styles.topicCards}>
              {topicHubs.map((item) => (
                <Link className={styles.topicCard} key={item.id} to={item.path}>
                  <span className={styles.topicTitle}>{item.title}</span>
                  <span className={styles.topicText}>{item.description}</span>
                  <span className={styles.topicCount}>
                    {t("topicCount", {
                      count: images.filter((image) =>
                        item.categoryTags.includes(image.tag) && (!item.difficulty || image.difficulty === item.difficulty)
                      ).length,
                    })}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {!category && newestImages.length > 0 && (
          <section className={styles.topicGrid} aria-labelledby="neueste-ausmalbilder">
            <h2 id="neueste-ausmalbilder" className={styles.sectionTitle}>
              {t("newestImagesTitle")}
            </h2>
            <p className={styles.sectionIntro}>{t("newestImagesText")}</p>
            <div className={styles.latestGrid}>
              {newestImages.map((image) => (
                <article className={styles.galleryCard} key={image.downloadUrl}>
                  <Link className={styles.galleryLink} to={getImagePagePath(image, locale)}>
                    <div className={styles.galleryMedia}>
                      <LazyImage
                        alt={image.description}
                        className={styles.colorPrintImage}
                        height={image.thumbnailHeight}
                        src={`/${image.thumbnailUrl}`}
                        title={image.title}
                        width={image.thumbnailWidth}
                      />
                    </div>
                    <div className={styles.galleryBody}>
                      <span className={styles.galleryTag}>
                        {categoryNames[image.tag] || image.tag}
                      </span>
                      <h3 className={styles.cardTitle}>{image.title}</h3>
                      <span className={styles.cardCta}>{t("openPreview")}</span>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </section>
        )}

        {pdfCategories.length > 0 && (
          <section className={styles.topicGrid} aria-labelledby="pdf-pakete">
            <h2 id="pdf-pakete" className={styles.sectionTitle}>
              {t("pdfPacksTitle")}
            </h2>
            <p className={styles.sectionIntro}>{t("pdfPacksText")}</p>
            <div className={styles.topicCards}>
              {pdfCategories.map((item) => (
                <a
                  className={styles.topicCard}
                  download
                  onClick={() => trackAction("pdf_pack_download", item, locale)}
                  href={`/${item.pdfUrl}`}
                  key={item.tag}
                >
                  <span className={styles.topicTitle}>{item.name}</span>
                  <span className={styles.topicText}>{item.description}</span>
                  <span className={styles.topicCount}>
                    {t("topicCount", {
                      count: item.pdfImageCount || categoryCounts[item.tag] || 0,
                    })}
                  </span>
                  <span className={styles.cardCta}>{t("downloadPdfPack")}</span>
                </a>
              ))}
            </div>
          </section>
        )}



        <section className={styles.seoPanel}>
          <h2 className={styles.sectionTitle}>{t("beforePrint")}</h2>
          <div className={styles.seoColumns}>
            <p>{t("beforePrintText1")}</p>
            <p>{t("beforePrintText2")}</p>
          </div>
        </section>

        <section className={styles.infoBand} aria-labelledby="druck-tipps">
          <h2 id="druck-tipps" className={styles.sectionTitle}>
            {t("printTips")}
          </h2>
          <div className={styles.guideGrid}>
            <article>
              <span>01</span>
              <h3>{t("printTip1Title")}</h3>
              <p>{t("printTip1Text")}</p>
            </article>
            <article>
              <span>02</span>
              <h3>{t("printTip2Title")}</h3>
              <p>{t("printTip2Text")}</p>
            </article>
            <article>
              <span>03</span>
              <h3>{t("printTip3Title")}</h3>
              <p>{t("printTip3Text")}</p>
            </article>
          </div>
        </section>



        {!category && (
          <section className={styles.faqPanel} aria-labelledby="faq">
            <h2 id="faq" className={styles.sectionTitle}>
              {t("questions")}
            </h2>
            <div className={styles.faqList}>
              {faqItems.map((item) => (
                <article key={item.question}>
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </article>
              ))}
            </div>
          </section>
        )}
      </section>
    </SiteShell>
  );
}

export default Home;
